import {Auth} from "types/auth"
import {useState} from "react"
import {fieldsService} from "services"
import {handleError} from "helpers"
import {Settings} from "types/settings"
import styles from "uiKit/StudentInfoPanel/StudentInfoPanel.module.css"
import {BaseInput, BaseTextArea} from "components/inputs"
import cx from "classnames"
import CircularSpin from "components/CircularSpin"
import {Icon} from "components/Icon"
import {Tooltip} from "antd"
import {useMutation} from "@tanstack/react-query"

type Props = {
    field: Settings.CustomField
    defaultValue: string | number
    student?: Auth.DepartmentStudent
    readOnly?: boolean
}

const CustomFieldContent = ({field, defaultValue, student, readOnly = false}: Props) => {
    const [isEditing, setEditing] = useState(false)
    const [value, setValue] = useState(defaultValue)
    const editable = !readOnly

    const updateCustomFieldsForProfileMutation = useMutation(
        (data: Settings.UpdateCustomFieldsPayload) => fieldsService.updateCustomFieldsForProfile(data),
        {
            onSuccess: () => setEditing(false),
            onError: (error) => handleError(error)
        }
    )

    const handleEditClick = () => {
        setEditing(true)
    }

    const renderInput = () => {
        switch (field.fieldType) {
            case Settings.CustomFieldType.Text:
                return <BaseInput value={value} onChange={setValue} className={styles.contentInput} />
            case Settings.CustomFieldType.Number:
                return <BaseInput value={value} type="number" onChange={setValue} className={styles.contentInput} />
            case Settings.CustomFieldType.Paragraph:
                return <BaseTextArea value={value} onChange={setValue} className={styles.contentInput} />
            default:
                return null
        }
    }

    const handleSaveClick = () => {
        updateCustomFieldsForProfileMutation.mutate({
            customFields: [
                {customFieldId: field.customFieldId, value, userId: student?.userId, profileId: student?.profileId}
            ]
        })
    }

    return (
        <div className={styles.contentWrap}>
            <div
                className={cx(styles.contentTitle, {[styles.editable]: editable})}
                onClick={editable ? handleEditClick : undefined}
                title={field.label}>
                {field.label}
            </div>
            {isEditing ? (
                <div className={styles.contentValueWrap}>
                    <div className={cx(styles.contentValue)}>{renderInput()}</div>
                    <div className={styles.contentActions}>
                        {updateCustomFieldsForProfileMutation.isLoading ? (
                            <CircularSpin size="small" />
                        ) : (
                            <div className={styles.contentAction} onClick={handleSaveClick}>
                                <Icon icon="CHECK_CIRCLE" />
                            </div>
                        )}
                    </div>
                </div>
            ) : (
                <Tooltip title={value} placement="topRight">
                    <div
                        className={cx(styles.contentValue, {[styles.editable]: editable})}
                        onClick={editable ? handleEditClick : undefined}>
                        <span className={styles.displayValue}>{value}</span>
                    </div>
                </Tooltip>
            )}
        </div>
    )
}

export default CustomFieldContent
